import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { previewLookbook } from '@/lib/lookbook-api';
import { fetchSubmissionThumbnailDataUrl } from '@/lib/submissions-api';
import { ArrowLeft, Image, Video } from 'lucide-react';

export default function HostLookbookPreviewPage() {
  const navigate = useNavigate();
  const { ready } = useRequireAuth();
  const [sp] = useSearchParams();
  const eventId = sp.get('event_id') || '';

  const [manifest, setManifest] = useState(null);
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [thumbs, setThumbs] = useState({});

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try { const res = await previewLookbook(eventId); setManifest(res.manifest); setSource(res.source); setError(null); }
    catch (e) { setError(e?.message || 'Preview failed.'); }
    finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  useEffect(() => {
    if (!manifest) return;
    const ids = [];
    if (manifest.hero_submission_id) ids.push(manifest.hero_submission_id);
    for (const s of manifest.sections || []) for (const it of s.items || []) ids.push(it.submission_id);
    (async () => { for (const id of ids) { if (thumbs[id]) continue; try { const url = await fetchSubmissionThumbnailDataUrl(id); if (url) setThumbs(p => ({ ...p, [id]: url })); } catch {} } })();
  }, [manifest]); // eslint-disable-line

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="host-lookbook-preview-screen">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="lookbook-preview-back">
        <ArrowLeft className="w-4 h-4" /> back to builder
      </button>

      {loading ? (
        <div className="flex justify-center py-8" data-testid="lookbook-preview-loading"><div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : error ? (
        <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434]" data-testid="lookbook-preview-error">{error}</div>
      ) : manifest ? (
        <div className="flex flex-col gap-4">
          <span className={`self-start px-2.5 py-0.5 rounded-full text-xs font-bold ${source === 'live' ? 'bg-[#E4F4EC] text-[#1F8A55]' : 'bg-[#EFF5FF] text-[#146BFF]'}`}>{source === 'live' ? 'LIVE PREVIEW' : 'DRAFT PREVIEW'}</span>

          {manifest.hero_submission_id && thumbs[manifest.hero_submission_id] ? (
            <img src={thumbs[manifest.hero_submission_id]} alt="" className="w-full h-[240px] rounded-xl object-cover" data-testid="lookbook-preview-hero" />
          ) : (
            <div className="w-full h-[200px] rounded-xl bg-[#F3F5F8] flex items-center justify-center"><Image className="w-8 h-8 text-[#5B6472]" /></div>
          )}

          <h1 className="text-3xl font-extrabold text-[#0B1B46]" data-testid="lookbook-preview-title">{manifest.title || 'Untitled Lookbook'}</h1>
          {manifest.subtitle && <p className="text-sm text-[#5B6472]" data-testid="lookbook-preview-subtitle">{manifest.subtitle}</p>}
          <div className="flex flex-wrap gap-2">
            <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">{manifest.photo_count} photos</span>
            <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">{manifest.video_count} videos</span>
            <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">{manifest.contributor_count} contributors</span>
          </div>

          {manifest.intro_text && <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 text-sm text-[#0B1B46] leading-relaxed" data-testid="lookbook-preview-intro">{manifest.intro_text}</div>}

          {(manifest.sections || []).map(sec => (
            <div key={sec.id} className="flex flex-col gap-3" data-testid={`lookbook-preview-section-${sec.id}`}>
              <h2 className="text-xl font-extrabold text-[#0B1B46] mt-2">{sec.title}</h2>
              <div className="flex flex-wrap gap-3">
                {(sec.items || []).map(it => (
                  <div key={it.submission_id} className="w-[156px]" data-testid={`lookbook-preview-item-${it.submission_id}`}>
                    {thumbs[it.submission_id] ? (
                      <img src={thumbs[it.submission_id]} alt="" className="w-[156px] h-[156px] rounded-lg object-cover" />
                    ) : (
                      <div className="w-[156px] h-[156px] rounded-lg bg-[#F3F5F8] flex items-center justify-center">
                        {it.media_kind === 'video' ? <Video className="w-5 h-5 text-[#5B6472]" /> : <Image className="w-5 h-5 text-[#5B6472]" />}
                      </div>
                    )}
                    {it.contributor_first_name && <p className="text-[11px] font-bold text-[#146BFF] mt-1" data-testid={`lookbook-preview-name-${it.submission_id}`}>— {it.contributor_first_name}</p>}
                    {it.caption && <p className="text-xs text-[#5B6472]">{it.caption}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {manifest.closing_text && <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 text-sm text-[#0B1B46] leading-relaxed" data-testid="lookbook-preview-closing">{manifest.closing_text}</div>}

          <div className="flex gap-3">
            <button onClick={() => navigate(-1)} className="flex-1 h-11 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg" data-testid="lookbook-preview-back-btn">Back to builder</button>
            {source !== 'live' && (
              <button onClick={() => navigate(`/host-lookbook-launch?event_id=${encodeURIComponent(eventId)}`)}
                className="flex-1 h-11 bg-[#146BFF] text-white font-semibold rounded-lg" data-testid="lookbook-preview-launch-btn">Launch settings →</button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
